import { useEffect, useMemo } from "react";

import { ApiProvider } from "@/data/api";
import { IdentityProvider, type IdentityServices } from "@/data/identity/context";
import { withSessionGuard } from "@/data/identity/guard";
import type { OrthosApi } from "@/data/types";
import { ToastProvider, TooltipProvider } from "@/ui";

import { AppRoutes } from "./AppRoutes";
import { ErrorBoundary } from "./errors/ErrorBoundary";

export interface AppProps {
  api: OrthosApi;
  identity: IdentityServices;
}

/** Providers shared by every page. Construction (config, API, identity) happens in main.tsx. */
export function App({ api, identity }: AppProps) {
  const { session } = identity;
  // A data call that finds the session ended shows the "Your session ended" dialog (FR-ADMIN-047).
  const guardedApi = useMemo(
    () => withSessionGuard(api, () => session.markExpired()),
    [api, session],
  );
  useEffect(() => session.start(), [session]);
  return (
    <ErrorBoundary>
      <IdentityProvider services={identity}>
        <ApiProvider api={guardedApi}>
          <TooltipProvider>
            <ToastProvider>
              <AppRoutes />
            </ToastProvider>
          </TooltipProvider>
        </ApiProvider>
      </IdentityProvider>
    </ErrorBoundary>
  );
}
